class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
      // Guard against double-binding when multiple search components wrap the same form
      // (e.g. predictive-search wrapping main-search on the search template)
      if (this.input.form && !this.input.form.hasAttribute('data-query-expand-bound')) {
        this.input.form.setAttribute('data-query-expand-bound', '');
        this.input.form.addEventListener('submit', this.expandQueryOnSubmit.bind(this));
      }
    }
  }

  // Shopify's search results page ignores prefix matching for SKU/model-style
  // terms and applies typo correction instead (e.g. "k707" returns K673/K762
  // products). An explicit trailing wildcard forces prefix matching, and OR
  // chaining lets digit-only queries (e.g. "913") match letter-prefixed model
  // codes (e.g. "M913"). Word-only queries are left untouched so shoppers
  // keep typo correction.
  static expandDigitQuery(query) {
    if (!/^\d+$/.test(query)) return null;
    // Merchant-configured model-code prefixes (theme settings → Search input),
    // e.g. "k, m, h" → 707 becomes "707* OR k707* OR m707* OR h707*".
    // Unset = try every letter
    const configured = (window.skuSearchPrefixes || '')
      .toLowerCase()
      .split(/[\s,]+/)
      .filter((prefix) => /^[a-z0-9]{1,4}$/.test(prefix));
    const prefixes = configured.length ? configured : 'abcdefghijklmnopqrstuvwxyz'.split('');
    const variants = [`${query}*`];
    for (const prefix of prefixes) {
      variants.push(`${prefix}${query}*`);
    }
    return variants.join(' OR ');
  }

  static expandSubmitQuery(query) {
    const trimmed = query.trim();
    if (!trimmed || /[*"']/.test(trimmed) || /\sOR\s/i.test(trimmed)) return query;

    // Digit-only queries (e.g. "707") submit unchanged so the URL stays
    // readable — the search page re-renders the grid with the expanded
    // query in the background (see main-search.js)
    if (/^\d+$/.test(trimmed)) return query;

    // Only wildcard the query when the last term looks like a SKU/model code
    // (contains a digit); plain words keep Shopify's typo correction
    const terms = trimmed.split(/\s+/);
    const lastTerm = terms[terms.length - 1];
    if (/\d/.test(lastTerm) && /[a-z0-9]$/i.test(lastTerm)) {
      return `${trimmed}*`;
    }
    return query;
  }

  expandQueryOnSubmit(event) {
    if (event.defaultPrevented) return;
    // Don't rewrite when a predictive suggestion is selected — its link is followed instead
    if (this.querySelector('[aria-selected="true"] a')) return;

    const expanded = SearchForm.expandSubmitQuery(this.input.value);
    if (expanded === this.input.value) return;

    // Navigate with the expanded q ourselves instead of rewriting the visible
    // input — otherwise the expansion flashes in the search bar and reappears
    // when the browser restores the page on back navigation
    const form = this.input.form;
    event.preventDefault();
    const params = new URLSearchParams(new FormData(form));
    params.set('q', expanded);
    const action = form.getAttribute('action') || '/search';
    window.location.assign(`${action.split('?')[0]}?${params.toString()}`);
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);
